import { resolveJsonPath } from '../../../shared/utils/jsonPath';
import { toEventEnvelope } from './eventDisplay';
import {
  MAX_INPUTS,
  PATH_REFUSED,
  emptyInput,
  emptySource,
  fromDatasetPath,
  isDatasetPath,
  pathProblem,
  patternProblems,
  primaryPath,
  sourceForm,
  sourceProblem,
  toColumnSource,
  toDatasetPath,
} from './datasetMappingUtils';
import type { ColumnRole, ColumnSource, InputForm, SourceForm } from './datasetMappingUtils';
import type { DatasetColumn, DatasetInput, EventDataset } from '../../../types/backend';
import type { LiveEvent } from '../../../types/frontend';

export type ColumnType = DatasetColumn['type'];

export const COLUMN_TYPES: ColumnType[] = ['text', 'number', 'timestamp', 'boolean', 'list', 'json'];

// The backend's STANDARD_COLUMNS: every dataset has them, required and in this order.
export const STANDARD_COLUMNS: DatasetColumn[] = [
  { name: 'customer', type: 'text', required: true },
  { name: 'account', type: 'text', required: true },
  { name: 'region', type: 'text', required: true },
  { name: 'resource', type: 'text', required: true },
  { name: 'event_time', type: 'timestamp', required: true },
  { name: 'event_id', type: 'text', required: true },
];

export const DEFAULT_ORDERING_FIELD = 'event_time';
const RESERVED_COLUMNS = ['row_position'];
const PER_EVENT_COLUMNS = ['event_id'];
const NAME_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
const BREAKING_PREFIX = 'Breaking change to ';
// The authorization wall's refusal: "You don't have permission to use events:CreateEventDataset".
const CEDAR_EVENTS_REFUSAL = /permission to use events:/;
const PREVIEW_NAME = 'preview';
// One day fits any retention ceiling the backend checks a draft against.
const PREVIEW_RETENTION_DAYS = 1;
const NAIVE_ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
// A key that is exactly Id or Arn, or ends in one after a lowercase letter or digit: FindingId, ResourceArn.
const IDENTITY_KEY = /^(?:Id|Arn)$|[a-z0-9](?:Id|Arn)$/;
const RESOURCE_SUGGESTION = '$.Resources[0].Id';
const PYDANTIC_HEADER = /^\d+ validation errors? for \w+$/;
const PYDANTIC_DETAIL = /^\s*(?:Value error, )?(.*?)\s*\[type=.*$/;
const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  inputs: 'Event pattern and columns',
  row_path: 'Row path',
  columns: 'Columns',
  key_fields: 'Key fields',
  ordering_field: 'Ordering field',
  keep_current_state: 'Keep current state',
  retention_days: 'Retention',
};

export type EditorMode = 'create' | 'edit';

export interface ColumnRow {
  name: string;
  type: ColumnType;
  required: boolean;
  /** Where the column reads from in each input, by the input's position. */
  sources: SourceForm[];
  /** An existing column of a saved dataset: changing it is a breaking change. */
  locked: boolean;
}

export interface DatasetFormState {
  name: string;
  /** One to four, each with its own pattern and resource path; each column has a source in every one. */
  inputs: InputForm[];
  rowPath: string;
  columns: ColumnRow[];
  /** False lets an event without a resource fit as an account-wide row; changing it on a saved dataset breaks it. */
  resourceRequired: boolean;
  keyFields: string[];
  orderingField: string;
  keepCurrentState: boolean;
  retentionDays: string;
}

export type DatasetFormAction =
  | { type: 'SET_FIELD'; field: keyof DatasetFormState; value: DatasetFormState[keyof DatasetFormState] }
  | { type: 'ADD_COLUMN'; required: boolean }
  | { type: 'UPDATE_COLUMN'; index: number; changes: Partial<ColumnRow> }
  | { type: 'REMOVE_COLUMN'; index: number }
  | { type: 'TOGGLE_KEY'; column: string }
  | { type: 'ADD_SAMPLE_COLUMN'; column: ColumnRow }
  | { type: 'USE_AS_KEY'; index: number }
  | { type: 'ADD_INPUT' }
  | { type: 'REMOVE_INPUT'; input: number }
  | { type: 'SET_INPUT_PATTERN'; input: number; pattern: Record<string, unknown> }
  | { type: 'SET_INPUT_RESOURCE'; input: number; path: string }
  | { type: 'SET_SOURCE'; column: number; input: number; source: SourceForm };

// Key and ordering fields name columns, so they follow a rename and let go of a column that is gone or optional.
// Only the column that alone holds a name owns its references: a standard name, an empty one or one two columns
// share belongs to no single column, so typing through it neither takes nor moves a key.
function ownsName(columns: ColumnRow[], index: number, name: string): boolean {
  return !!name
    && !STANDARD_COLUMNS.some((c) => c.name === name)
    && !columns.some((c, i) => i !== index && c.name === name);
}

function renameColumnReferences(state: DatasetFormState, from: string, to: string): DatasetFormState {
  if (from === to) return state;
  return {
    ...state,
    keyFields: state.keyFields.map((k) => (k === from ? to : k)),
    orderingField: state.orderingField === from ? to : state.orderingField,
  };
}

function dropColumnReferences(state: DatasetFormState, name: string): DatasetFormState {
  return {
    ...state,
    keyFields: state.keyFields.filter((k) => k !== name),
    orderingField: state.orderingField === name ? DEFAULT_ORDERING_FIELD : state.orderingField,
  };
}

export function datasetFormReducer(state: DatasetFormState, action: DatasetFormAction): DatasetFormState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'ADD_COLUMN':
      return {
        ...state,
        columns: [
          ...state.columns,
          { name: '', type: 'text', required: action.required, sources: state.inputs.map(emptySource), locked: false },
        ],
      };
    case 'UPDATE_COLUMN': {
      const before = state.columns[action.index];
      const after = { ...before, ...action.changes };
      const columns = state.columns.map((c, i) => (i === action.index ? after : c));
      if (!ownsName(state.columns, action.index, before.name)) return { ...state, columns };
      if (!after.required || !ownsName(columns, action.index, after.name)) {
        return dropColumnReferences({ ...state, columns }, before.name);
      }
      return renameColumnReferences({ ...state, columns }, before.name, after.name);
    }
    case 'REMOVE_COLUMN': {
      const columns = state.columns.filter((_, i) => i !== action.index);
      const { name } = state.columns[action.index];
      return ownsName(state.columns, action.index, name) ? dropColumnReferences({ ...state, columns }, name) : { ...state, columns };
    }
    case 'TOGGLE_KEY':
      return withKeyFields(
        state,
        state.keyFields.includes(action.column)
          ? state.keyFields.filter((k) => k !== action.column)
          : [...state.keyFields, action.column],
      );
    case 'ADD_SAMPLE_COLUMN':
      return { ...state, columns: [...state.columns, action.column] };
    case 'USE_AS_KEY': {
      const target = state.columns[action.index];
      const columns = state.columns.map((c, i) => (i === action.index ? { ...c, required: true } : c));
      const keyFields = state.keyFields.includes(target.name) ? state.keyFields : [...state.keyFields, target.name];
      return withKeyFields({ ...state, columns }, keyFields);
    }
    case 'ADD_INPUT':
      if (state.inputs.length >= MAX_INPUTS) return state;
      return {
        ...state,
        inputs: [...state.inputs, emptyInput()],
        columns: state.columns.map((c) => ({ ...c, sources: [...c.sources, emptySource()] })),
      };
    case 'REMOVE_INPUT':
      // A saved input is identified by its position, so only one added since the save goes.
      if (state.inputs.length === 1 || state.inputs[action.input].locked) return state;
      return {
        ...state,
        inputs: state.inputs.filter((_, n) => n !== action.input),
        columns: state.columns.map((c) => ({ ...c, sources: c.sources.filter((_, n) => n !== action.input) })),
      };
    case 'SET_INPUT_PATTERN':
      return { ...state, inputs: state.inputs.map((i, n) => (n === action.input ? { ...i, eventPattern: action.pattern } : i)) };
    case 'SET_INPUT_RESOURCE':
      return { ...state, inputs: state.inputs.map((i, n) => (n === action.input ? { ...i, resourcePath: action.path } : i)) };
    case 'SET_SOURCE':
      return {
        ...state,
        columns: state.columns.map((c, i) => (i === action.column
          ? { ...c, sources: c.sources.map((s, n) => (n === action.input ? action.source : s)) }
          : c)),
      };
  }
}

// The first key that names the thing a row is about (not the per-event event_id) turns on the current state.
function withKeyFields(state: DatasetFormState, keyFields: string[]): DatasetFormState {
  const identifies = (keys: string[]) => keys.some((k) => !PER_EVENT_COLUMNS.includes(k));
  const keepCurrentState = state.keepCurrentState || (!identifies(state.keyFields) && identifies(keyFields));
  return { ...state, keyFields, keepCurrentState };
}

/** A dataset started from an Explorer event: its source and detail-type as the pattern, the event as the sample. */
export interface DatasetSeed {
  eventPattern: Record<string, unknown>;
  sampleEvent: Record<string, unknown>;
}

/**
 * The Explorer's timestamp is Logs Insights time, which carries no zone and is UTC. The dataset
 * preview refuses a sample time without a zone, so a naive date-time gets `Z`; a zoned one is kept.
 */
export function zonedUtcTime(timestamp: string): string {
  if (NAIVE_ISO_DATE_TIME.test(timestamp)) return `${timestamp}Z`;
  return timestamp;
}

export function datasetSeedFromEvent(event: LiveEvent): DatasetSeed {
  const envelope = toEventEnvelope(event);
  return {
    eventPattern: { source: [event.source], 'detail-type': [event.detail_type] },
    sampleEvent: { ...envelope, time: zonedUtcTime(event.timestamp) },
  };
}

export function buildDatasetForm(
  initial: EventDataset | null,
  mode: EditorMode,
  startPattern: Record<string, unknown> = {},
): DatasetFormState {
  if (!initial) {
    return {
      name: '',
      inputs: [emptyInput(startPattern)],
      rowPath: '',
      columns: [],
      resourceRequired: true,
      keyFields: [],
      orderingField: DEFAULT_ORDERING_FIELD,
      keepCurrentState: false,
      retentionDays: '',
    };
  }
  if (initial.inputs.length === 0) {
    throw new Error(`Event dataset '${initial.name}' has no input; a dataset takes one to ${MAX_INPUTS}`);
  }
  const locked = mode === 'edit';
  const sourceOf = (input: DatasetInput, n: number, column: string): SourceForm => {
    const mapping: Record<string, ColumnSource | undefined> = input.mapping;
    const source = mapping[column];
    if (source === undefined) {
      throw new Error(`Event dataset '${initial.name}' input ${n + 1} gives no source for column '${column}'`);
    }
    return sourceForm(source, locked);
  };
  const inputs = initial.inputs.map((input, n): InputForm => {
    const resource = input.mapping.resource;
    if (resource !== undefined && typeof resource !== 'string') {
      throw new Error(`Event dataset '${initial.name}' input ${n + 1} gives the resource no plain path`);
    }
    return { eventPattern: input.event_pattern, resourcePath: resource ? fromDatasetPath(resource) : '', locked };
  });
  return {
    name: initial.name,
    inputs,
    rowPath: initial.row_path ? fromDatasetPath(initial.row_path) : '',
    columns: initial.columns.map((c) => ({
      ...c,
      sources: initial.inputs.map((input, n) => sourceOf(input, n, c.name)),
      locked,
    })),
    resourceRequired: initial.resource_required ?? true,
    keyFields: initial.key_fields,
    orderingField: initial.ordering_field ?? DEFAULT_ORDERING_FIELD,
    keepCurrentState: initial.keep_current_state,
    retentionDays: String(initial.retention_days),
  };
}

/** Inline name check while typing: refuses, never rewrites. Empty is left to the save. */
export function nameProblem(name: string): string | undefined {
  if (!name || NAME_PATTERN.test(name)) return undefined;
  if (/[A-Z]/.test(name)) return 'Use lowercase letters only';
  return 'Lowercase letters, digits and underscore, starting with a letter, at most 64';
}

/** Standard columns and the operator's required columns: the only ones a key or ordering may name. */
export function requiredColumnNames(form: DatasetFormState): string[] {
  const operator = form.columns.filter((c) => c.required && c.name).map((c) => c.name);
  return [...STANDARD_COLUMNS.map((c) => c.name), ...operator];
}

/** Key fields that change with every event or every moment, so each row becomes its own key. */
export function keyFieldWarnings(form: DatasetFormState): string[] {
  const types = new Map<string, ColumnType>([
    ...STANDARD_COLUMNS.map((c) => [c.name, c.type] as const),
    ...form.columns.map((c) => [c.name, c.type] as const),
  ]);
  return form.keyFields.filter((k) => types.get(k) === 'timestamp' || PER_EVENT_COLUMNS.includes(k));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function singular(key: string): string {
  if (/ies$/.test(key)) return `${key.slice(0, -3)}y`;
  if (/[^s]s$/.test(key)) return key.slice(0, -1);
  return key;
}

/** Each list of objects in the sample, outside other lists, as a row path: `detail.findings` gives one row per finding. */
export function rowPathSuggestions(sample: Record<string, unknown>): { path: string; label: string }[] {
  const out: { path: string; label: string }[] = [];
  const walk = (node: Record<string, unknown>, path: string) => {
    for (const [key, value] of Object.entries(node)) {
      const next = `${path}.${key}`;
      if (isPlainObject(value)) walk(value, next);
      else if (Array.isArray(value) && isPlainObject(value[0]) && isDatasetPath(next, false)) {
        out.push({ path: next, label: `One row per ${singular(key).toLowerCase()}` });
      }
    }
  };
  walk(sample, '$');
  return out;
}

/** A Security Hub style resource on the row, offered as the resource path. */
export function resourcePathSuggestion(row: Record<string, unknown>): string | undefined {
  return typeof resolveJsonPath(RESOURCE_SUGGESTION, row) === 'string' ? RESOURCE_SUGGESTION : undefined;
}

function pathKeys(formPath: string): string[] {
  return toDatasetPath(formPath).replace(/^\$event\./, '').replace(/\[[0-9]+\]/g, '').split('.').filter(Boolean);
}

function snakeCase(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
}

function guessType(value: unknown): ColumnType {
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (Array.isArray(value)) return 'list';
  if (isPlainObject(value)) return 'json';
  return 'text';
}

/**
 * A column for a value picked from the sample row, named after the keys of its path (`$.Severity.Label` gives
 * `severity_label`), dropping leading keys while the name is too long. A one-key name that is taken becomes
 * `row_` and the key; a name still taken gets a number.
 */
export function columnFromSamplePath(path: string, value: unknown, form: DatasetFormState, input = 0): ColumnRow {
  const taken = new Set([...STANDARD_COLUMNS.map((c) => c.name), ...RESERVED_COLUMNS, ...form.columns.map((c) => c.name)]);
  const keys = pathKeys(path).map(snakeCase).filter(Boolean);
  let named = keys.join('_') || 'value';
  for (let from = 1; !NAME_PATTERN.test(named) && from < keys.length; from += 1) named = keys.slice(from).join('_');
  const qualified = keys.length > 1 ? named : `row_${named}`;
  let name = [named, qualified].find((n) => !taken.has(n) && NAME_PATTERN.test(n)) ?? qualified;
  for (let n = 2; taken.has(name); n += 1) name = `${qualified}_${n}`;
  const sources = form.inputs.map((_, n): SourceForm => (n === input ? { kind: 'path', path } : emptySource()));
  return { name, type: guessType(value), required: false, sources, locked: false };
}

/** Named columns whose path ends in an Id or Arn key and that are not keys yet, offered as the key. */
export function keySuggestions(form: DatasetFormState): { index: number; name: string }[] {
  return form.columns
    .map((c, index) => ({ c, index }))
    .filter(({ c }) => c.name && !form.keyFields.includes(c.name) && IDENTITY_KEY.test(pathKeys(primaryPath(c.sources[0])).pop() ?? ''))
    .map(({ c, index }) => ({ index, name: c.name }));
}

/** A backend refusal as a plain sentence: a Pydantic validation error becomes each field and its message. */
export function plainPreviewError(message: string): string {
  const lines = message.split('\n');
  if (!PYDANTIC_HEADER.test(lines[0]?.trim() ?? '')) return message;
  const sentences: string[] = [];
  let field: string | null = null;
  for (const line of lines.slice(1)) {
    const detail = PYDANTIC_DETAIL.exec(line);
    if (detail) {
      const text = detail[1].replace(/\.$/, '');
      // The preview request carries the definition as `draft`, so its fields read `draft.key_fields`.
      const loc = field?.replace(/^draft\./, '');
      sentences.push(loc ? `${FIELD_LABELS[loc.split('.')[0]] ?? loc}: ${text}.` : `${text}.`);
      field = null;
    } else if (line.trim() && !line.startsWith(' ')) {
      field = line.trim();
    }
  }
  return sentences.length > 0 ? sentences.join(' ') : 'The draft is not valid; check the fields of the form.';
}

/** A column's role in the form: its type, and whether it is required, a key or the ordering field. */
export function columnRole(form: DatasetFormState, column: ColumnRow): ColumnRole {
  const identifies = form.keyFields.includes(column.name) || form.orderingField === column.name;
  return { type: column.type, alwaysFilled: column.required || identifies, identifies, hasRowPath: !!form.rowPath.trim() };
}

export function validateDatasetForm(form: DatasetFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!form.name) errors.name = 'Name is required';
  else if (nameProblem(form.name)) errors.name = nameProblem(form.name) as string;

  for (const [n, problem] of Object.entries(patternProblems(form.inputs))) errors[`input.${n}.eventPattern`] = problem;
  if (form.rowPath.trim() && !toDatasetPath(form.rowPath)) errors.rowPath = 'Row path names no field';
  else if (form.rowPath.trim() && !isDatasetPath(form.rowPath, false)) errors.rowPath = PATH_REFUSED;

  const standard = STANDARD_COLUMNS.map((c) => c.name);
  const seen = new Set<string>();
  form.columns.forEach((c, i) => {
    if (!c.name) errors[`column.${i}.name`] = 'Name is required';
    else if (standard.includes(c.name)) errors[`column.${i}.name`] = `${c.name} is a standard column`;
    else if (RESERVED_COLUMNS.includes(c.name)) errors[`column.${i}.name`] = `${c.name} is reserved`;
    else if (!NAME_PATTERN.test(c.name)) errors[`column.${i}.name`] = 'Lowercase letters, digits and underscore';
    else if (seen.has(c.name)) errors[`column.${i}.name`] = `${c.name} is used twice`;
    seen.add(c.name);
    const role = columnRole(form, c);
    c.sources.forEach((source, n) => {
      const problem = sourceProblem(source, role);
      if (problem) errors[`input.${n}.column.${i}.source`] = problem;
    });
  });

  form.inputs.forEach((input, n) => {
    const problem = input.resourcePath.trim() ? pathProblem(input.resourcePath) : undefined;
    if (problem) errors[`input.${n}.resourcePath`] = problem;
  });

  const required = requiredColumnNames(form);
  if (form.keyFields.length === 0) errors.keyFields = 'Pick at least one key field';
  const notRequired = form.keyFields.filter((k) => !required.includes(k));
  if (notRequired.length > 0) errors.keyFields = `Key fields must be required columns: ${notRequired.join(', ')}`;
  if (!required.includes(form.orderingField)) errors.orderingField = 'The ordering field must be a required column';

  if (retentionProblem(form.retentionDays)) errors.retentionDays = 'Retention must be a positive number of days';
  return errors;
}

function retentionProblem(retentionDays: string): boolean {
  const retention = Number(retentionDays);
  return !Number.isInteger(retention) || retention <= 0;
}

export function toEventDataset(form: DatasetFormState, customerId: string): EventDataset {
  const inputs = form.inputs.map((input, n): DatasetInput => {
    const mapping: Record<string, ColumnSource> = {};
    for (const c of form.columns) mapping[c.name] = toColumnSource(c.sources[n], c.type);
    if (input.resourcePath.trim()) mapping.resource = toDatasetPath(input.resourcePath);
    return { event_pattern: input.eventPattern, mapping };
  });
  return {
    customer_id: customerId,
    name: form.name,
    inputs,
    row_path: form.rowPath.trim() ? toDatasetPath(form.rowPath) : undefined,
    columns: form.columns.map(({ name, type, required }) => ({ name, type, required })),
    // Required is the backend's default, so only an account-wide dataset says so, as ordering_field does.
    ...(form.resourceRequired ? {} : { resource_required: false }),
    key_fields: form.keyFields,
    ordering_field: form.orderingField === DEFAULT_ORDERING_FIELD ? undefined : form.orderingField,
    keep_current_state: form.keepCurrentState,
    retention_days: Number(form.retentionDays),
    source: 'events',
  };
}

/**
 * The draft a preview sends. A name or a retention changes no rows, so an unnamed or misnamed draft, or
 * one without a valid retention, carries a stand-in the backend accepts; the preview keeps every other
 * check a save makes.
 */
export function toPreviewDraft(form: DatasetFormState, customerId: string): EventDataset {
  const draft = toEventDataset(form, customerId);
  return {
    ...draft,
    name: form.name && !nameProblem(form.name) ? draft.name : PREVIEW_NAME,
    retention_days: retentionProblem(form.retentionDays) ? PREVIEW_RETENTION_DAYS : draft.retention_days,
  };
}

/** The save checks without the name and retention rules: a preview runs before either is set. */
export function validatePreviewForm(form: DatasetFormState): Record<string, string> {
  const errors = validateDatasetForm(form);
  delete errors.name;
  delete errors.retentionDays;
  return errors;
}

/** The element a column path reads from: the first item under the row path, or the event. */
export function rowSample(sample: Record<string, unknown>, rowPath: string): Record<string, unknown> {
  if (!rowPath.trim()) return sample;
  const rows = resolveJsonPath(rowPath.trim(), sample);
  const first = Array.isArray(rows) ? rows[0] : undefined;
  return typeof first === 'object' && first !== null && !Array.isArray(first) ? (first as Record<string, unknown>) : {};
}

/** The Cedar wall's refusal of an events action, as the level a dataset write needs; any other message is kept. */
export function plainWriteRefusal(message: string): string {
  return CEDAR_EVENTS_REFUSAL.test(message) ? 'You need Admin on this customer to do this' : message;
}

// The backend names a non-additive change with this prefix (BreakingChangeError); it carries no code of its own.
export function isBreakingChange(message: string): boolean {
  return message.startsWith(BREAKING_PREFIX);
}
